import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { FileDownloadOutlined, RefreshOutlined } from '@mui/icons-material';
import { getPbjExportRequest, resetPbjExport } from 'src/redux/actions/pbjExportAction';
import {
  PBJ_PROGRESS_COMPLETE_HOLD_MS,
  PBJ_PROGRESS_TICK_MS,
} from 'src/services/pbjService/constants';
import { TPbjFormat } from 'src/services/pbjService/types';
import {
  FfyQuarter,
  defaultFfyQuarter,
  ffyQuarterLabel,
  ffyQuarterValue,
  parseFfyQuarterValue,
  selectableFfyQuarters,
} from './ffy-quarters';
import { pbjProgressPercent } from './pbj-progress';
import { pbjWarningHeadline, pbjWarningLines } from './pbj-warnings';

/**
 * RA-2027 — quarterly CMS Payroll-Based Journal export.
 *
 * Two things about this card differ from SLM / UB04 / Census on purpose:
 *
 * - **No Patients filter, and no facility choice — the file is always the page's facility.** PBJ is
 *   facility-level, and a facility may not download another facility's labor data, so the scope is
 *   not the user's to pick: the card takes the uuid from the page's facility selector and sends it
 *   on every request (RA-2114). The card used to offer an "All facilities" option, which let anyone
 *   sitting on one building pull the whole chain's hours. Enterprise-wide filing is still a real
 *   need for a chain, but it belongs behind a role check in the backend, not in a dropdown here.
 *   The guard that carries the fix lives in `requestExport`: `getPbjExport` reads the *absence* of
 *   `facility_uuid` as "every facility", so a request that merely omits the key is the old bug
 *   wearing a new coat. With no uuid to send there is no narrower request to make — so the card
 *   makes none, and both buttons are disabled to say so.
 * - **XML and CSV are not interchangeable.** CMS accepts XML only. The zip is what gets uploaded to
 *   iQIES; the CSV is what a DOR opens in Excel to check hours first. A user who uploads the CSV
 *   gets a rejected federal filing, so the CSV button says so and there is no "download both".
 */
const PbjDownload = ({ now }: { now?: Date }) => {
  const dispatch = useDispatch();
  const pbjExport = useSelector((state: any) => state.getPbjExportReducer);
  //  Same source the SLM / UB04 / Census cards on this page use to name the selected facility.
  const assignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  //  The page's facility selector, which writes the chosen uuid here. Exactly one uuid, always —
  //  there is no "all facilities" sentinel it can hold, and this card wants none.
  const facilityUuid = localStorage.getItem('facilityId');

  //  Injectable clock. The quarter list and the default selection both depend on today's date, and
  //  a test that cannot pin the date can only assert the code against itself.
  const reference = useMemo(() => now ?? new Date(), [now]);
  const quarters = useMemo(() => selectableFfyQuarters(reference), [reference]);

  const [selected, setSelected] = useState<FfyQuarter>(() => defaultFfyQuarter(reference));
  const [progress, setProgress] = useState<number>(0);
  const [lastFormat, setLastFormat] = useState<TPbjFormat | null>(null);

  //  Identity of the download already handed to the browser, so a re-render cannot download twice.
  const deliveredRef = useRef<unknown>(null);

  //  The 100% hold's timer id. In a ref rather than closed over by the delivery effect's cleanup —
  //  see that effect for why a cleanup there can never let this timer fire.
  const holdTimerRef = useRef<number | null>(null);

  const clearHold = useCallback(() => {
    if (holdTimerRef.current === null) return;
    window.clearTimeout(holdTimerRef.current);
    holdTimerRef.current = null;
  }, []);

  const loading = Boolean(pbjExport?.loading);
  const warningCount = Number(pbjExport?.warningCount ?? 0);
  const warnings = (pbjExport?.warnings ?? {}) as Record<string, number>;

  //  Read straight off the reducer, and read here rather than beside the JSX because the effects
  //  below need them: these three are what the reducer sets on a request that has finished without
  //  producing a file. `isNoData` is the 404 carrying PBJ_NO_DATA_FOR_QUARTER, which the reducer
  //  keeps deliberately separate from `isError`; `isTimeout` arrives with `isError` set, so the
  //  generic error branch subtracts it.
  const showNoData = Boolean(pbjExport?.isNoData);
  const showTimeout = Boolean(pbjExport?.isTimeout);
  const showError = Boolean(pbjExport?.isError) && !showTimeout;

  //  Every way a request can end without a file. Delivery is the fourth terminal state and is not
  //  in here: it clears itself through the hold below, and it leaves no alert to sit beside.
  const settled = showNoData || showTimeout || showError;

  //  Name of the facility the page is on, so the caption can say which building's hours these are.
  //  Optional-chained the whole way: this card renders before the facility dropdown has resolved,
  //  and a name is a nicety here — the uuid is what actually scopes the request.
  const facilityName = useMemo(() => {
    const facilities = assignedFacilities?.response?.data?.data;
    if (!Array.isArray(facilities) || !facilityUuid) return '';
    const match = facilities.find((facility: any) => facility?.uuid === facilityUuid);
    return typeof match?.name === 'string' ? match.name.trim() : '';
  }, [assignedFacilities, facilityUuid]);

  //  Ease toward the ceiling while the request is in flight. Nothing in here can reach 100.
  useEffect(() => {
    if (!loading) return undefined;

    //  A new request supersedes the previous file's hold, which would otherwise fire part-way into
    //  this one and blank the bar for a tick.
    clearHold();

    const startedAt = Date.now();
    setProgress(pbjProgressPercent(0));

    const timer = window.setInterval(() => {
      setProgress(pbjProgressPercent(Date.now() - startedAt));
    }, PBJ_PROGRESS_TICK_MS);

    return () => window.clearInterval(timer);
  }, [loading, clearHold]);

  //  The response landed: snap to 100, hand over the bytes, then clear. This is the only place
  //  100 is ever set.
  useEffect(() => {
    const download = pbjExport?.response;
    if (!download || deliveredRef.current === download) return undefined;
    deliveredRef.current = download;

    setProgress(100);

    const url = window.URL.createObjectURL(
      new Blob([download.data], { type: download.contentType })
    );
    const link = document.createElement('a');
    link.href = url;
    //  Filename straight from the server's Content-Disposition — it owns the naming.
    link.setAttribute('download', download.filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);

    //  Drop the stored blob NOW, not on a timer. The reset used to sit inside the hold below,
    //  which meant navigating away inside the hold window left the bytes in the store: on the
    //  next mount `deliveredRef` is fresh, this effect sees a non-null response, and the browser
    //  downloads the file a second time with nobody having asked for it.
    dispatch(resetPbjExport());

    //  Hold 100% briefly so the snap is actually seen, then take the bar off screen entirely.
    //
    //  The id goes in a ref and is cancelled on unmount only — never from this effect's cleanup.
    //  The `resetPbjExport` dispatched immediately above nulls `pbjExport.response`, which is this
    //  effect's own dependency, so React tears the effect down and re-runs it on the very next
    //  render. A cleanup that cancelled the timer would therefore cancel it every time, before it
    //  could ever fire: the bar stayed at a full 100% for as long as the card stayed mounted, with
    //  no alert beside it to explain what had happened. That is the full-width bar QA screenshotted
    //  (RA-2114).
    clearHold();
    holdTimerRef.current = window.setTimeout(() => {
      holdTimerRef.current = null;
      setProgress(0);
    }, PBJ_PROGRESS_COMPLETE_HOLD_MS);

    return undefined;
  }, [pbjExport?.response, dispatch, clearHold]);

  //  Settled with no file: end the animation now, in the same commit that puts the alert up.
  //
  //  Nothing used to do this. The interval effect above stops ticking when `loading` drops, but it
  //  leaves `progress` at whatever it had reached, and `showProgress` was `progress > 0` — so the
  //  bar and "Preparing your file" stayed on screen underneath the result. QA caught it at ~10%
  //  above "No labor hours were found for Q3 2026", and the timeout and error paths leaked it the
  //  same way. Zeroing rather than merely hiding is what keeps the next download from picking up
  //  where this one died.
  useEffect(() => {
    if (!settled) return;
    clearHold();
    setProgress(0);
  }, [settled, clearHold]);

  //  Unmount is the one place the hold may legitimately be dropped: the bytes are already with the
  //  browser and the store was emptied at delivery, so there is nothing left for it to finish.
  useEffect(() => clearHold, [clearHold]);

  //  A message belongs to the quarter that produced it.
  const handleQuarterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseFfyQuarterValue(event.target.value);
    if (!parsed) return;
    setSelected(parsed);
    setProgress(0);
    dispatch(resetPbjExport());
  };

  const requestExport = useCallback(
    (format: TPbjFormat) => {
      //  A hard stop, not a fallback. `getPbjExport` treats a missing `facility_uuid` as "every
      //  facility in the enterprise", so falling through to a request without it would hand the
      //  user the whole chain's hours — the exact thing RA-2114 exists to stop. There is no
      //  narrower request available with no uuid in hand, so dispatch nothing and let the disabled
      //  buttons and the caption below explain why.
      if (!facilityUuid) return;

      setLastFormat(format);
      dispatch(
        getPbjExportRequest({
          year: selected.year,
          quarter: selected.quarter,
          format,
          //  Unconditional, and never spread. `facilityUuid` is optional on the request type only
          //  because the service still supports an unscoped call; this card never makes one.
          facilityUuid,
        })
      );
    },
    [dispatch, selected, facilityUuid]
  );

  //  `!settled` is belt and braces on top of the effect above: whatever order the effects run in,
  //  and whatever a future dependency change does to them, the render itself cannot put a progress
  //  bar and a result on screen together.
  const showProgress = progress > 0 && !settled;
  const showWarnings = warningCount > 0;

  //  No facility uuid means no request this card is allowed to make, so say so on the controls
  //  rather than letting the click land and go nowhere.
  const downloadDisabled = loading || !facilityUuid;

  return (
    //  `mt` here, not in `DownloadCard`. The card's `CardContent` is a flex *item*, not a flex
    //  container, so the `mt: 'auto'` it puts on this slot resolves to 0 and the controls butt
    //  straight into the description — worst on this card, whose description wraps to two lines and
    //  whose outlined quarter select carries its floating label on the top border (RA-2114).
    //  Fixing it centrally would re-bottom-align every other download card, so keep it local.
    <Box sx={{ mt: 2 }}>
      {/*
        Two cells, not three: the quarter select, then BOTH buttons as a single unit.

        This grid lives in a `sm={6}` card, so the row has ~700px and a page-level breakpoint says
        nothing about it — the wrap is driven by the card, not the viewport. Three sibling cells fit
        at design width and stopped fitting at real width, and a flex row wraps one item at a time:
        CSV dropped to its own line and left the primary button orphaned beside the select, which
        reads as broken alignment rather than a layout. Wrapping the pair in one cell means the row
        has only two things to break between, so the wrapped state is the designed one — select on
        top, both buttons together underneath, XML still first and still contained.

        `nowrap` inside the pair is the point of the cell: the two buttons stay side by side and
        move as one. Widths come from `sx` rather than from breakpoints for the same reason the
        wrap does, except at `xs`, where the viewport genuinely is the constraint and the select
        goes full-width instead of holding a 240px floor the screen cannot afford.
      */}
      <Grid
        container
        spacing={2}
        alignItems="flex-end"
      >
        <Grid
          item
          xs={12}
          sm="auto"
        >
          <TextField
            select
            label="Federal fiscal quarter"
            size="small"
            variant="outlined"
            value={ffyQuarterValue(selected)}
            onChange={handleQuarterChange}
            disabled={loading}
            sx={{ width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}
            inputProps={{ 'aria-label': 'Federal fiscal quarter' }}
          >
            {quarters.map((quarter) => (
              <MenuItem
                key={ffyQuarterValue(quarter)}
                value={ffyQuarterValue(quarter)}
              >
                {ffyQuarterLabel(quarter)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid
          item
          xs={12}
          sm="auto"
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              disabled={downloadDisabled}
              startIcon={<FileDownloadOutlined />}
              variant="contained"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                //  Matches the small outlined select exactly, so the three controls read as one
                //  row of equal-height controls instead of nearly-aligned ones. `flex-end` on the
                //  container already put their bottoms on a common line; this puts their tops
                //  there too.
                height: 40,
                ':hover': { background: '#6366F1' },
              }}
              onClick={() => requestExport('xml')}
            >
              XML for CMS
            </Button>

            <Button
              disabled={downloadDisabled}
              startIcon={<FileDownloadOutlined />}
              variant="outlined"
              size="small"
              sx={{ whiteSpace: 'nowrap', height: 40 }}
              onClick={() => requestExport('csv')}
            >
              CSV (for review)
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 1,
          color: 'text.secondary',
          //  Facility names are free text and run long ("Alaska Daylight Time - US Alaska" is a
          //  real one). The caption wraps and is never truncated — the name is the half of the
          //  sentence that says which building's hours these are, so clipping it defeats the
          //  point. `anywhere` covers the name with no spaces in it to break at, which is the only
          //  version of this that could push past the card's edge.
          overflowWrap: 'anywhere',
        }}
      >
        {/*
          Named where the name has resolved, because "this facility only" on a page with a facility
          switcher is worth pinning down. The unnamed branch is a different sentence rather than the
          same one with a hole in it: this card can render before the facility dropdown responds,
          and a caption built around a missing name reads worse than one that never claims it.

          It used to add "the rest of the enterprise is left out of this file". That warned about a
          choice the user had just made; with the choice gone it only advertises data they are not
          entitled to — and in the no-facility state it sat directly above "Choose a facility at
          the top of the page first", asserting a scope and denying one in consecutive sentences.
        */}
        {facilityName ? `${facilityName} only. ` : 'This facility only. '}
        CMS accepts the XML only — the CSV is for checking hours before you file.
      </Typography>

      {!facilityUuid && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
        >
          Choose a facility at the top of the page first — this file is always for one facility.
        </Typography>
      )}

      {showProgress && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            aria-label="Preparing your PBJ file"
          />
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
          >
            Preparing your file — this usually takes a few seconds.
          </Typography>
        </Box>
      )}

      {showNoData && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
        >
          No labor hours were found for {ffyQuarterLabel(selected)}. Nothing to file for that
          quarter.
        </Alert>
      )}

      {showTimeout && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              startIcon={<RefreshOutlined />}
              onClick={() => requestExport(lastFormat ?? 'xml')}
            >
              Try again
            </Button>
          }
        >
          This took longer than expected and timed out. Trying again rebuilds the file from scratch.
        </Alert>
      )}

      {showError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
        >
          Something went wrong preparing the PBJ file. Please try again, or let us know via Intercom
          if it keeps happening.
        </Alert>
      )}

      {showWarnings && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
        >
          <AlertTitle sx={{ fontSize: 14 }}>{pbjWarningHeadline(warningCount)}</AlertTitle>
          {/*
            Counts only — never the detail behind them. The detail carries provider and patient
            ids, and this surface is PHI-adjacent.
          */}
          {pbjWarningLines(warnings).map((line) => (
            <Typography
              key={line}
              variant="body2"
            >
              {line}
            </Typography>
          ))}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1 }}
          >
            Fix these in Restore and download again — CMS will reject a submission with hours
            missing.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default PbjDownload;
